export const RESULT_ORDER = [
  "summary",
  "visual",
  "structure",
  "rewrite",
  "original",
] as const;

export type ResultKey = (typeof RESULT_ORDER)[number];
export type FileKind = "video" | "audio" | "doc";
export type Scenario =
  | "normal"
  | "nospeech"
  | "asr-fail"
  | "video-fail"
  | "rewrite-fail";

export interface TranscriptionFile {
  name: string;
  type: FileKind;
  seconds: number;
  bytes: number;
  chars?: number;
  pages?: number;
  sample?: boolean;
  manual?: boolean;
  text?: string;
  noSpeech?: boolean;
}

export interface StructureRow {
  segment: string;
  location: string;
  mainContent: string;
  expressionRole: string;
}

export const STRUCTURE_COLUMN_LABELS = {
  segment: "片段",
  location: "内容定位",
  mainContent: "主要内容",
  expressionRole: "表达作用",
} as const;

export interface ResultItem {
  status: "success" | "failed" | "skipped" | "pending";
  html?: string;
  plain?: string;
  basis?: string;
  structureRows?: StructureRow[];
  prompt?: string;
  reason?: string;
  updated?: number;
}

export interface JobStep {
  key: string;
  label: string;
  status: "waiting" | "running" | "success" | "failed" | "skipped";
  amount: number;
  reason?: string;
}

export interface JobState {
  id: number;
  keys: ResultKey[];
  steps: JobStep[];
  index: number;
  due: number;
  estimate: number;
  scenario: Scenario;
  base: { original?: boolean; video?: boolean };
  failed: string[];
  paid: boolean;
}

export interface WorkRecord {
  workId: string;
  createdAt: number;
  file: TranscriptionFile;
  selected: ResultKey[];
  prompt: string;
  cache: { original?: boolean; video?: boolean };
  results: Partial<Record<ResultKey, ResultItem>>;
  active: ResultKey;
  job: JobState | null;
  charged: number;
  lastCharge?: number;
  lastScenario?: Scenario;
  warnings?: string[];
  lastKeys?: ResultKey[];
  scenario: Scenario;
  isTemplate?: boolean;
  templateKey?: FileKind;
  templateId?: number;
  taskStatus?: string;
  progress?: number;
  currentStep?: string;
  totalConsumedPoint?: number;
  generatedResultCount?: number;
}

export const TYPE_LABEL: Record<FileKind, string> = {
  video: "视频",
  audio: "音频",
  doc: "文档",
};

export const RESULT_META: Record<ResultKey, [string, string, string]> = {
  original: ["提取原文", "忠实还原语音或文档内容", "file"],
  visual: ["画面概述", "了解场景、动作和重要变化", "video"],
  summary: ["总结", "内容摘要与核心要点", "list"],
  structure: ["文案结构拆解", "看懂内容如何组织与表达", "layers"],
  rewrite: ["重写", "按你的要求生成新文案", "edit"],
};

export function availableResults(file?: TranscriptionFile | null): ResultKey[] {
  if (!file) return ["original", "summary", "structure", "rewrite"];
  return file.type === "video"
    ? ["original", "visual", "summary", "structure", "rewrite"]
    : ["original", "summary", "structure", "rewrite"];
}

export function dependencies(
  file: TranscriptionFile,
  keys: ResultKey[],
  cache: { original?: boolean; video?: boolean } = {},
) {
  const set = new Set(keys);
  const needsOriginal = [...set].some((k) => k !== "visual");
  const needsVideo =
    file.type === "video" && (set.has("visual") || set.has("summary"));
  return {
    original: needsOriginal && !cache.original,
    video: needsVideo && !cache.video,
  };
}

export function quote(
  file: TranscriptionFile | null | undefined,
  keys: ResultKey[],
  cache: { original?: boolean; video?: boolean } = {},
) {
  if (!file || !keys.length) return { total: 0, lines: [] as Array<{ key: string; label: string; amount: number }> };
  const need = dependencies(file, keys, cache);
  const m = (file.seconds || 0) / 60;
  const c = file.chars || 0;
  const lines: Array<{ key: string; label: string; amount: number }> = [];
  if (need.original && !file.noSpeech) {
    lines.push({
      key: "original",
      label: file.type === "doc" ? "文档提取" : "提取原文",
      amount: file.type === "doc" ? 1 : m / 3,
    });
  }
  if (need.video) {
    lines.push({ key: "video", label: "视频理解（含画面概述）", amount: m });
  }
  for (const k of ["summary", "structure", "rewrite"] as ResultKey[]) {
    if (
      keys.includes(k) &&
      !(file.noSpeech && (k !== "summary" || file.type === "audio"))
    ) {
      lines.push({
        key: k,
        label: RESULT_META[k][0],
        amount:
          file.type === "doc"
            ? c / (k === "rewrite" ? 1000 : 2000)
            : m / (k === "rewrite" ? 3 : 5),
      });
    }
  }
  const raw = lines.reduce((a, x) => a + x.amount, 0);
  return { total: raw > 0 ? Math.max(1, Math.ceil(raw - 1e-9)) : 0, lines };
}

export function validateFile(
  name: string,
  bytes: number,
  seconds?: number,
  chars?: number,
  pages?: number,
): string | null {
  const ext = name.split(".").pop()?.toLowerCase() || "";
  const type: FileKind | null = ["mp4", "mov"].includes(ext)
    ? "video"
    : ["mp3", "wav", "m4a"].includes(ext)
      ? "audio"
      : ["pdf", "docx", "txt"].includes(ext)
        ? "doc"
        : null;
  if (!type) {
    return "不支持此格式，请上传 MP4、MOV、MP3、WAV、M4A、PDF、DOCX 或 TXT。";
  }
  const max = type === "video" ? 500 : type === "audio" ? 200 : ext === "txt" ? 1 : 20;
  if (!bytes) return "文件为空，请重新选择。";
  if (bytes > max * 1e6) return `文件超过 ${max}MB，请缩减后上传。`;
  if (
    type !== "doc" &&
    seconds !== undefined &&
    (!Number.isFinite(seconds) || seconds <= 0)
  ) {
    return "无法读取有效时长，请检查文件是否损坏。";
  }
  if (seconds !== undefined && seconds > (type === "video" ? 1800 : 7200)) {
    return `时长超过${type === "video" ? "30" : "120"}分钟，请截取后上传。`;
  }
  if (chars !== undefined && chars > 30000) {
    return `正文共 ${chars.toLocaleString()} 字符，超过30,000字符，请缩减后上传。`;
  }
  if (pages !== undefined && pages > 100) {
    return "PDF超过100页，请拆分后上传。";
  }
  return null;
}

export function detectFileKind(name: string): FileKind | null {
  const ext = name.split(".").pop()?.toLowerCase() || "";
  if (["mp4", "mov"].includes(ext)) return "video";
  if (["mp3", "wav", "m4a"].includes(ext)) return "audio";
  if (["pdf", "docx", "txt"].includes(ext)) return "doc";
  return null;
}

export function workStatus(work: WorkRecord) {
  const results = Object.values(work.results || {});
  const success = results.filter((r) => r.status === "success").length;
  const status = String(work.taskStatus || "").toUpperCase();
  if (status === "QUEUED" || status === "PROCESSING" || work.job) {
    const progress = Math.max(
      0,
      Math.min(99, Number.isFinite(Number(work.progress)) ? Number(work.progress) : 0),
    );
    return {
      kind: "running" as const,
      label: status === "QUEUED" ? "排队中" : "生成中",
      progress,
      message: work.currentStep || work.job?.steps[work.job.index]?.label || "正在整理结果",
      success,
    };
  }
  if (status === "PARTIAL_SUCCESS") {
    return {
      kind: "partial" as const,
      label: "部分完成",
      progress: 100,
      message: "部分内容未生成，可查看详情后重试",
      success,
    };
  }
  if (status === "FAILED") {
    return {
      kind: "failed" as const,
      label: "生成失败",
      progress: 100,
      message: "部分内容未生成，可查看详情后重试",
      success,
    };
  }
  if (status === "SUCCESS") {
    return {
      kind: "success" as const,
      label: "已完成",
      progress: 100,
      message: `已生成 ${work.generatedResultCount ?? success} 项内容`,
      success,
    };
  }
  const failed =
    (work.warnings?.length || 0) > 0 ||
    results.some((r) => r.status !== "success");
  return {
    kind: failed ? (success ? "partial" : "failed") : "success",
    label: failed ? (success ? "部分完成" : "生成失败") : "已完成",
    progress: 100,
    message: failed
      ? "部分内容未生成，可查看详情后重试"
      : `已生成 ${work.generatedResultCount ?? success} 项内容`,
    success,
  };
}

export function captureWork(data: WorkRecord): WorkRecord {
  const keys: Array<keyof WorkRecord> = [
    "workId",
    "createdAt",
    "file",
    "selected",
    "prompt",
    "cache",
    "results",
    "active",
    "job",
    "charged",
    "lastCharge",
    "lastScenario",
    "warnings",
    "lastKeys",
    "scenario",
  ];
  const work = {} as WorkRecord;
  for (const key of keys) {
    (work as any)[key] = data[key];
  }
  return JSON.parse(JSON.stringify(work));
}

export function upsertWork(works: WorkRecord[], work: WorkRecord) {
  const index = works.findIndex((w) => w.workId === work.workId);
  if (index < 0) return [work, ...works];
  return works.map((w, i) => (i === index ? work : w));
}

export function exportText(work: WorkRecord, keys?: ResultKey[]) {
  return [
    work.file.name,
    ...RESULT_ORDER.filter(
      (k) =>
        (!keys || keys.includes(k)) && work.results[k]?.status === "success",
    ).map((k) => `${RESULT_META[k][0]}\n\n${work.results[k]?.plain || ""}`),
  ].join("\n\n──────────\n\n");
}

export function formatSize(bytes: number) {
  return bytes >= 1e6
    ? `${(bytes / 1e6).toFixed(1)}MB`
    : `${Math.ceil(bytes / 1000)}KB`;
}

export function formatDuration(sec: number) {
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}分${s % 60 ? String(s % 60).padStart(2, "0") + "秒" : "钟"}`;
}

export function fileMeta(file: TranscriptionFile) {
  return [
    TYPE_LABEL[file.type],
    file.type === "doc"
      ? `${(file.chars || 0).toLocaleString()}字符${file.pages ? ` · ${file.pages}页` : ""}`
      : formatDuration(file.seconds),
    formatSize(file.bytes),
  ].join(" · ");
}

export function rateLabel(file: TranscriptionFile, key: string) {
  if (file.type === "doc") {
    return (
      {
        original: "1积分 / 文件",
        summary: "1积分 / 2,000字符",
        structure: "1积分 / 2,000字符",
        rewrite: "1积分 / 1,000字符",
      } as Record<string, string>
    )[key];
  }
  return (
    {
      original: "1积分 / 3分钟",
      video: "1积分 / 分钟",
      summary: "1积分 / 5分钟",
      structure: "1积分 / 5分钟",
      rewrite: "1积分 / 3分钟",
    } as Record<string, string>
  )[key];
}

export function escapeHtml(value: unknown) {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      (
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }) as Record<string, string>
      )[c],
  );
}
